import { useState } from 'react';
import Avatar from '../components/Avatar';
import { updateProfile } from '../data/userService';

const TAG_OPTIONS = [
  'Long-term', 'ETFs', 'Global', 'Monthly investing', 'Dividends',
  'Individual stocks', 'Tech', 'Property', 'Index funds', 'Learning in public',
];
const BIO_MAX = 160;

/**
 * Required step. Name and bio are required; everything else is optional.
 * Saves the fields plus profileComplete: true.
 */
export default function ProfileStep({ uid, initial = {}, email, onSaved, onLogOut }) {
  const [form, setForm] = useState({
    name: initial.name || '',
    location: initial.location || '',
    investingSince: initial.investingSince || '',
    bio: initial.bio || '',
    credentials: [...(initial.credentials || [])],
    tags: [...(initial.tags || [])],
  });
  const [newCred, setNewCred] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [tried, setTried] = useState(false);

  const missing = { name: !form.name.trim(), bio: !form.bio.trim() };
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const addCred = () => {
    const value = newCred.trim();
    if (!value || form.credentials.includes(value)) return;
    setForm({ ...form, credentials: [...form.credentials, value] });
    setNewCred('');
  };
  const removeCred = (c) => setForm({ ...form, credentials: form.credentials.filter((x) => x !== c) });
  const toggleTag = (t) =>
    setForm({ ...form, tags: form.tags.includes(t) ? form.tags.filter((x) => x !== t) : [...form.tags, t] });

  const submit = async (e) => {
    e.preventDefault();
    setTried(true);
    if (missing.name || missing.bio) {
      document.getElementById(missing.name ? 'ob-name' : 'ob-bio')?.focus();
      return;
    }
    setBusy(true);
    setError('');
    const fields = {
      ...form,
      name: form.name.trim(),
      location: form.location.trim(),
      investingSince: form.investingSince.trim(),
      bio: form.bio.trim(),
    };
    try {
      await updateProfile(uid, { ...fields, profileComplete: true });
      onSaved(fields);
    } catch (err) {
      console.error('Saving profile failed', err);
      setError('We couldn’t save your profile. Check your connection and try again.');
      setBusy(false);
    }
  };

  const preview = { name: form.name || '?', photoUrl: initial.photoUrl, avatarColor: initial.avatarColor };

  return (
    <form className="ob-body" onSubmit={submit} noValidate>
      <div className="ob-intro">
        <h2 id="ob-title">Set up your profile</h2>
        <p>This is what other members see before they follow you. You can change it any time.</p>
      </div>

      <div className="ob-photo">
        <Avatar user={preview} size={64} />
        <div>
          <b>{form.name || 'Your name'}</b>
          {email && <span>{email}</span>}
        </div>
      </div>

      <div className="ob-row">
        <div className="ob-field ob-grow">
          <label htmlFor="ob-name">Display name <span className="ob-req">required</span></label>
          <input
            id="ob-name"
            autoComplete="name"
            value={form.name}
            onChange={set('name')}
            aria-invalid={tried && missing.name}
            aria-describedby={tried && missing.name ? 'ob-name-err' : undefined}
          />
          {tried && missing.name && <span id="ob-name-err" className="ob-field-err">Add the name other members will see.</span>}
        </div>
        <div className="ob-field ob-grow">
          <label htmlFor="ob-loc">Location</label>
          <input id="ob-loc" placeholder="e.g. London" value={form.location} onChange={set('location')} />
        </div>
        <div className="ob-field ob-since">
          <label htmlFor="ob-since">Investing since</label>
          <input id="ob-since" inputMode="numeric" placeholder="e.g. 2021" value={form.investingSince} onChange={set('investingSince')} />
        </div>
      </div>

      <div className="ob-field">
        <label htmlFor="ob-bio">Bio <span className="ob-req">required</span></label>
        <textarea
          id="ob-bio"
          rows={3}
          maxLength={BIO_MAX}
          placeholder="One or two lines: how you invest and what you’re working towards."
          value={form.bio}
          onChange={set('bio')}
          aria-invalid={tried && missing.bio}
          aria-describedby={tried && missing.bio ? 'ob-bio-err' : undefined}
        />
        <div className="ob-field-foot">
          {tried && missing.bio ? <span id="ob-bio-err" className="ob-field-err">Add a line or two about yourself.</span> : <span />}
          <span className="ob-count">{form.bio.length}/{BIO_MAX}</span>
        </div>
      </div>

      <div className="ob-field">
        <span className="ob-label">Credentials</span>
        <span className="ob-hint">What helps others trust your view: years investing, your job, qualifications, awards.</span>
        {form.credentials.length > 0 && (
          <ul className="ob-chips">
            {form.credentials.map((c) => (
              <li key={c} className="ob-chip">
                {c}
                <button type="button" className="ob-chip__x" aria-label={`Remove ${c}`} onClick={() => removeCred(c)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="ob-add">
          <label htmlFor="ob-cred" className="ob-sr">Add a credential</label>
          <input
            id="ob-cred"
            placeholder="e.g. Chartered accountant"
            value={newCred}
            onChange={(e) => setNewCred(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addCred();
              }
            }}
          />
          <button type="button" className="ob-btn ob-btn--ghost" onClick={addCred}>Add</button>
        </div>
      </div>

      <fieldset className="ob-field ob-fieldset">
        <legend className="ob-label">Investing style</legend>
        <div className="ob-tags">
          {TAG_OPTIONS.map((t) => {
            const on = form.tags.includes(t);
            return (
              <button key={t} type="button" aria-pressed={on} className={`ob-tag${on ? ' ob-tag--on' : ''}`} onClick={() => toggleTag(t)}>
                {t}
              </button>
            );
          })}
        </div>
      </fieldset>

      {error && <p className="ob-error" role="alert">{error}</p>}

      <div className="ob-actions">
        <button type="button" className="ob-link" onClick={onLogOut} disabled={busy}>Log out</button>
        <button type="submit" className="ob-btn ob-btn--primary" disabled={busy}>
          {busy ? 'Saving…' : 'Continue'}
        </button>
      </div>
    </form>
  );
}
